/**
 * QuoteTimeline — lifecycle events (mock-aligned vertical log).
 */

import React, { useMemo } from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { format } from "date-fns";
import { buildQuoteTimeline } from "../services/quoteOperationalStatus.js";

const QuoteTimeline = ({ quote, compact = false }) => {
  const events = useMemo(() => buildQuoteTimeline(quote), [quote]);

  const body = (
    <>
      {!compact && (
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }} gutterBottom>
          Quote timeline
        </Typography>
      )}
      {events.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No timeline events yet.
        </Typography>
      ) : (
        <Box
          component="ol"
          sx={{
            m: 0,
            pl: 0,
            listStyle: "none",
            borderLeft: "2px solid",
            borderColor: "divider",
            ml: 1,
          }}
        >
          {events.map((event, index) => {
            const isLast = index === events.length - 1;
            return (
              <Box
                component="li"
                key={`${event.key}-${event.at.toISOString()}`}
                sx={{ position: "relative", pl: 3, pb: isLast ? 0 : 2 }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    left: -5,
                    top: 6,
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: isLast ? "success.main" : "primary.main",
                    boxShadow: isLast
                      ? "0 0 0 4px rgba(46,125,50,0.18)"
                      : "none",
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: "monospace" }}
                >
                  {format(event.at, "HH:mm")}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {event.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {format(event.at, "d MMM yyyy")}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );

  if (compact) return <Box data-testid="quote-timeline">{body}</Box>;

  return (
    <Card
      elevation={0}
      sx={{
        mb: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
      }}
      data-testid="quote-timeline"
    >
      <CardContent sx={{ p: 2.5 }}>{body}</CardContent>
    </Card>
  );
};

export default QuoteTimeline;
